import { defineComponent } from "vue";
import { IconPlusLg, IconMinusLg, CdrButton } from "@rei/cedar";
import { CompassSpinner } from "@rei/spinner";
const _sfc_main = defineComponent({
  name: "ImageZoom",
  components: {
    CompassSpinner,
    IconPlusLg,
    IconMinusLg,
    CdrButton
  },
  props: {
    imageCaption: {
      type: String,
      default: ""
    },
    imageSrc: {
      type: String,
      required: true
    },
    touchLib: {
      type: Function,
      required: false,
      default: null
    }
  },
  emits: ["imageZoomClose", "nextImage", "prevImage"],
  data() {
    return {
      disableAnimation: false,
      isLoading: true,
      isMouseDown: false,
      top: 0,
      left: 0,
      startY: 0,
      startX: 0,
      zoomLevel: 1,
      showZoomLevel: true,
      mc: null,
      pinchStart: 1,
      maxZoom: 3,
      minZoom: 1,
      zoomTextTimer: null,
      isZooming: false,
      shouldPan: true
    };
  },
  computed: {
    isCentered() {
      const {
        image,
        zoomContainer
      } = this.$refs;
      const horiz = this.top === (image.clientWidth - zoomContainer.clientWidth) / 2;
      const vert = this.top === (image.clientHeight - zoomContainer.clientHeight) / 2;
      return horiz && vert;
    },
    getHelperText() {
      return this.zoomLevel > 1 ? "use arrow keys, mouse, or touchscreen to move around" : "use buttons or pinch to zoom image";
    }
  },
  watch: {
    zoomLevel() {
      this.showZoomLevel = true;
      if (this.zoomTextTimer) window.clearTimeout(this.zoomTextTimer);
      this.zoomTextTimer = window.setTimeout(() => {
        this.showZoomLevel = false;
      }, 200);
    },
    async imageSrc(newCurrent) {
      const newImg = document.createElement("img");
      newImg.src = newCurrent;
      const imageState = newImg.complete === true;
      this.isLoading = !imageState;
      if (this.zoomLevel !== 1 || !this.isCentered) {
        this.disableAnimation = true;
        await this.$nextTick();
        this.zoomLevel = 1;
        this.centerImage();
        await this.$nextTick();
        this.disableAnimation = false;
      }
    },
    touchLib(lib, oldLib) {
      if (oldLib === null) this.registerMultitouch();
    }
  },
  async mounted() {
    this.showZoomLevel = false;
    if (this.touchLib !== null) this.registerMultitouch();
  },
  beforeUnmount() {
    if (this.mc) {
      this.mc.off("pinchstart");
      this.mc.off("pinch");
      this.mc.off("pinchend");
      this.mc.off("pan");
      this.mc.off("panstart");
      this.mc.off("panend");
    }
  },
  methods: {
    registerMultitouch() {
      const Hammer = this.touchLib;
      this.mc = new Hammer(this.$refs.imageScroller);
      this.mc.get("pinch").set({
        enable: true
      });
      this.mc.get("swipe").set({
        direction: Hammer.DIRECTION_ALL
      });
      this.mc.on("pinchstart", (e) => this.handlePinch(e, "start"));
      this.mc.on("pinch", (e) => this.handlePinch(e));
      this.mc.on("pinchend", (e) => this.handlePinch(e, "end"));
      this.mc.on("panstart", (e) => this.handlePan(e, "start"));
      this.mc.on("pan", (e) => this.handlePan(e.srcEvent));
      this.mc.on("panend", (e) => this.handlePan(e, "end"));
    },
    centerImage() {
      const {
        image,
        zoomContainer
      } = this.$refs;
      if (image && zoomContainer) {
        this.left = (image.clientWidth - zoomContainer.clientWidth) / 2;
        this.top = (image.clientHeight - zoomContainer.clientHeight) / 2;
      }
    },
    handleKeyDown(e) {
      const {
        key
      } = e;
      const moveImage = (direction) => {
        const INCREMENT = 50;
        if (direction === "ArrowLeft") this.left += INCREMENT;
        if (direction === "ArrowRight") this.left -= INCREMENT;
        if (direction === "ArrowUp") this.top += INCREMENT;
        if (direction === "ArrowDown") this.top -= INCREMENT;
      };
      switch (key) {
        case "ArrowDown":
        case "ArrowUp":
        case "ArrowLeft":
        case "ArrowRight":
          e.preventDefault();
          moveImage(key);
          break;
        case "Add":
        case "Subtract":
        case "+":
        case "-":
          if (e.metaKey) {
            e.preventDefault();
            const fn = key === "Add" || key === "+" ? this.clickPlus : this.clickMinus;
            fn();
          }
          break;
      }
    },
    handleMouseDown() {
      this.isMouseDown = true;
    },
    handleMouseUp() {
      this.isMouseDown = false;
    },
    handleWheel(e) {
      e.preventDefault();
      if (e.metaKey) {
        const newZoom = this.zoomLevel - e.deltaY / 200;
        if (newZoom <= this.minZoom) {
          this.zoomLevel = this.minZoom;
          this.centerImage();
        } else if (newZoom >= this.maxZoom) {
          this.zoomLevel = this.maxZoom;
        } else {
          this.zoomLevel = newZoom;
        }
      }
    },
    handleLoad() {
      this.isLoading = false;
      this.centerImage();
    },
    handlePinch(event, condition) {
      const {
        maxZoom,
        minZoom
      } = this;
      const {
        scale,
        center
      } = event;
      if (condition === "start") {
        this.pinchStart = this.zoomLevel;
        this.startY = center.y;
        this.startX = center.x;
        this.disableAnimation = true;
        this.isZooming = true;
        this.shouldPan = false;
      } else if (condition === "end") {
        this.disableAnimation = false;
        this.isZooming = false;
        if (this.zoomLevel === minZoom) this.centerImage();
      } else {
        const newZoom = this.pinchStart * scale;
        const deltaY = (this.startY - center.y) / this.zoomLevel;
        const deltaX = (this.startX - center.x) / this.zoomLevel;
        this.startY = center.y;
        this.startX = center.x;
        this.top -= deltaY;
        this.left -= deltaX;
        if (newZoom > maxZoom) {
          this.zoomLevel = maxZoom;
        } else if (newZoom < minZoom) {
          this.zoomLevel = minZoom;
        } else {
          this.zoomLevel = this.pinchStart * scale;
        }
      }
    },
    // @ts-expect-error: e as type of HammerInput may not always be the event type here
    // consider updating to accept a consistent event
    handlePan(e, condition) {
      if (this.zoomLevel !== 1) {
        if (condition === "start") {
          this.shouldPan = !this.isZooming;
          this.disableAnimation = true;
          this.startY = e.srcEvent.y;
          this.startX = e.srcEvent.x;
        } else if (condition === "end") {
          this.disableAnimation = false;
        } else if (this.shouldPan) {
          const deltaY = (this.startY - e.y) / this.zoomLevel;
          const deltaX = (this.startX - e.x) / this.zoomLevel;
          this.startY = e.y;
          this.startX = e.x;
          const newTop = this.top - deltaY;
          const newLeft = this.left - deltaX;
          this.top = newTop;
          this.left = newLeft;
        }
      }
    },
    clickPlus() {
      let newZoom = 1;
      if (this.zoomLevel + 1 > this.maxZoom) newZoom = this.maxZoom;
      if (this.zoomLevel < this.maxZoom) newZoom = Math.round(this.zoomLevel + 1);
      this.zoomLevel = newZoom;
    },
    clickMinus() {
      const newZoom = this.zoomLevel - 1 < this.minZoom ? this.minZoom : Math.round(this.zoomLevel - 1);
      this.zoomLevel = newZoom;
      this.centerImage();
    },
    swipeLeft() {
      this.$emit("prevImage");
    },
    swipeRight() {
      this.$emit("nextImage");
    },
    close() {
      this.$emit("imageZoomClose");
    }
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=ImageZoom2.mjs.map
